import { ModelViewerElement } from '@google/model-viewer';
import { HotspotData } from '@google/model-viewer/lib/features/annotation';
import * as H from 'preact/hooks';
import { logError } from '../../store/actions/logging';
import { useDispatch } from '../hooks/redux';

/**
 * This hook is responsible for drawing the dimension line SVGs that mark the bounding box of the model
 */

export const useDrawDimensionLines = (modelViewer?: ModelViewerElement) => {
    const dispatch = useDispatch();
    const [showHotspots, setShowHotspots] = H.useState(false);
    const [showDimensions, setShowDimensions] = H.useState(false);
    const [readyToRender, setReadyToRender] = H.useState(false);

    const setDimensionsEnabled = (enabled: boolean) => {
        setShowDimensions(enabled);
        setShowHotspots(enabled);
    };

    H.useEffect(() => {
        modelViewer?.querySelectorAll('button').forEach(hotspot => {
            if (showHotspots) {
                hotspot.classList.remove('hide');
            } else {
                hotspot.classList.add('hide');
            }
        });
    }, [showHotspots]);

    const drawLine = (
        svgLine: SVGLineElement,
        dotHotspot1: HotspotData | null,
        dotHotspot2: HotspotData | null,
        dimensionHotspot?: HotspotData | null,
    ) => {
        if (dotHotspot1 && dotHotspot2) {
            svgLine.setAttribute('x1', `${dotHotspot1.canvasPosition.x}`);
            svgLine.setAttribute('y1', `${dotHotspot1.canvasPosition.y}`);
            svgLine.setAttribute('x2', `${dotHotspot2.canvasPosition.x}`);
            svgLine.setAttribute('y2', `${dotHotspot2.canvasPosition.y}`);

            // hide the dimension line if either showDimensions is false or the provided dimension hotspot (label) is not facing the camera (hidden)
            if ((dimensionHotspot && !dimensionHotspot.facingCamera) || !showDimensions) {
                svgLine.classList.add('hide');
            } else {
                svgLine.classList.remove('hide');
            }
        }
    };

    const startUpdateDimensionLinesLoop = () => {
        if (modelViewer) {
            const lines = modelViewer.querySelectorAll('line');

            drawLine(
                lines[0],
                modelViewer.queryHotspot('hotspot-dot+X-Y+Z'),
                modelViewer.queryHotspot('hotspot-dot+X-Y-Z'),
                modelViewer.queryHotspot('hotspot-dim+X-Y'),
            );
            drawLine(
                lines[1],
                modelViewer.queryHotspot('hotspot-dot+X-Y-Z'),
                modelViewer.queryHotspot('hotspot-dot+X+Y-Z'),
                modelViewer.queryHotspot('hotspot-dim+X-Z'),
            );
            drawLine(
                lines[2],
                modelViewer.queryHotspot('hotspot-dot+X+Y-Z'),
                modelViewer.queryHotspot('hotspot-dot-X+Y-Z'),
                null,
            ); // always visible
            drawLine(
                lines[3],
                modelViewer.queryHotspot('hotspot-dot-X+Y-Z'),
                modelViewer.queryHotspot('hotspot-dot-X-Y-Z'),
                modelViewer.queryHotspot('hotspot-dim-X-Z'),
            );
            drawLine(
                lines[4],
                modelViewer.queryHotspot('hotspot-dot-X-Y-Z'),
                modelViewer.queryHotspot('hotspot-dot-X-Y+Z'),
                modelViewer.queryHotspot('hotspot-dim-X-Y'),
            );

            // use requestAnimationFrame loop to keep in lockstep with renderer
            requestAnimationFrame(() => startUpdateDimensionLinesLoop());
        }
    };

    H.useEffect(() => {
        if (readyToRender) {
            startUpdateDimensionLinesLoop();
        }
    }, [showDimensions, readyToRender]);

    H.useEffect(() => {
        if (modelViewer && !readyToRender) {
            // fetch elements and fail early if we can't find any

            const labelBottomRight = document.getElementById('dim+X-Y');
            const labelCenterRight = document.getElementById('dim+X-Z');
            const labelTopRear = document.getElementById('dim+Y-Z');
            const labelCenterLeft = document.getElementById('dim-X-Z');
            const labelBottomLeft = document.getElementById('dim-X-Y');

            const lineXTopPoint1 = document.getElementById('dot-X+Y-Z');
            const lineXTopPoint2 = document.getElementById('dot+X+Y-Z');
            const lineYLeftPoint1 = document.getElementById('dot-X-Y-Z');
            const lineYLeftPoint2 = document.getElementById('dot-X+Y-Z');
            const lineYRightPoint1 = document.getElementById('dot+X-Y-Z');
            const lineYRightPoint2 = document.getElementById('dot+X+Y-Z');
            const lineZLeftPoint1 = document.getElementById('dot-X-Y-Z');
            const lineZLeftPoint2 = document.getElementById('dot-X-Y+Z');
            const lineZRightPoint1 = document.getElementById('dot+X-Y-Z');
            const lineZRightPoint2 = document.getElementById('dot+X-Y+Z');

            if (
                !labelBottomRight ||
                !labelCenterRight ||
                !labelTopRear ||
                !labelCenterLeft ||
                !labelBottomLeft ||
                !lineXTopPoint1 ||
                !lineXTopPoint2 ||
                !lineYLeftPoint1 ||
                !lineYLeftPoint2 ||
                !lineYRightPoint1 ||
                !lineYRightPoint2 ||
                !lineZLeftPoint1 ||
                !lineZLeftPoint2 ||
                !lineZRightPoint1 ||
                !lineZRightPoint2
            ) {
                dispatch(logError('cannot find required hotspot elements!'));
                return;
            }

            setReadyToRender(true);
        }
    }, [modelViewer, readyToRender]);

    return {
        setDimensionsEnabled,
    };
};
